import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ShiftResponseDto } from "../../shared/dtos/shift/ShiftResponseDto";
import { ShiftSummaryDto } from "../../shared/dtos/shift/ShiftSummaryDto";
import { PaginationDto } from "../../shared/dtos/common/PaginationDto";
import { ServerStatusPayload } from "../../shared/interfaces/ServerStatusPayload";

export interface ShiftState {
  shifts: ShiftSummaryDto[];
  currentShift: ShiftResponseDto | null;
  hasOpenShift: boolean | null;
  pagination: Partial<PaginationDto>;
  loadingFetchAll: boolean;
  loadingFetchCurrent: boolean;
  loadingOpen: boolean;
  loadingClose: boolean;
  loadingHasOpenShift: boolean;
  errorFetchAll: string | null;
  errorFetchCurrent: string | null;
  errorOpen: string | null;
  errorClose: string | null;
  serverStatus: ServerStatusPayload | null;
}

const initialState: ShiftState = {
  shifts: [],
  currentShift: null,
  hasOpenShift: null,
  pagination: {},
  loadingFetchAll: false,
  loadingFetchCurrent: false,
  loadingOpen: false,
  loadingClose: false,
  loadingHasOpenShift: false,
  errorFetchAll: null,
  errorFetchCurrent: null,
  errorOpen: null,
  errorClose: null,
  serverStatus: null,
};

export const shiftSlice = createSlice({
  name: "shift",
  initialState,
  reducers: {
    setLoadingFetchAll(state) {
      state.loadingFetchAll = true;
      state.errorFetchAll = null;
    },
    setErrorFetchAll(state, action: PayloadAction<string | null>) {
      state.loadingFetchAll = false;
      state.errorFetchAll = action.payload;
    },
    setShifts(state, action: PayloadAction<ShiftSummaryDto[]>) {
      state.loadingFetchAll = false;
      state.shifts = action.payload;
    },

    setLoadingFetchCurrent(state) {
      state.loadingFetchCurrent = true;
      state.errorFetchCurrent = null;
    },
    setErrorFetchCurrent(state, action: PayloadAction<string | null>) {
      state.loadingFetchCurrent = false;
      state.errorFetchCurrent = action.payload;
    },
    setCurrentShift(state, action: PayloadAction<ShiftResponseDto | null>) {
      state.loadingFetchCurrent = false;
      state.currentShift = action.payload;
    },

    setLoadingOpen(state) {
      state.loadingOpen = true;
      state.errorOpen = null;
    },
    setErrorOpen(state, action: PayloadAction<string | null>) {
      state.loadingOpen = false;
      state.errorOpen = action.payload;
    },
    openShiftSuccess(state, action: PayloadAction<ShiftSummaryDto>) {
      state.loadingOpen = false;
      state.shifts = [action.payload, ...state.shifts];
      state.hasOpenShift = true;
    },

    setLoadingClose(state) {
      state.loadingClose = true;
      state.errorClose = null;
    },
    setLoadingHasOpenShift(state) {
      state.loadingHasOpenShift = true;
    },
    setErrorClose(state, action: PayloadAction<string | null>) {
      state.loadingClose = false;
      state.errorClose = action.payload;
    },
    closeShiftSuccess(state, action: PayloadAction<ShiftSummaryDto>) {
      state.loadingClose = false;
      state.shifts = state.shifts.map((s) =>
        s.id === action.payload.id ? action.payload : s,
      );
      if (state.currentShift && state.currentShift.id === action.payload.id)
        state.currentShift = null;
      state.hasOpenShift = false;
    },

    setHasOpenShift(state, action: PayloadAction<boolean>) {
      state.loadingHasOpenShift = false;
      state.hasOpenShift = action.payload;
    },
    setPagination(state, action: PayloadAction<Partial<PaginationDto>>) {
      state.pagination = action.payload;
    },
    setServerStatus(state, action: PayloadAction<ServerStatusPayload | null>) {
      state.serverStatus = action.payload;
    },
  },
});

export const shiftActions = shiftSlice.actions;
export default shiftSlice.reducer;
